import React, { useEffect, useState } from "react";
import IntlMessages from "util/IntlMessages";
import { Card, Table, Row, Col, Button, Tooltip, Dropdown, Space, Modal, Upload, message, Select } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { saveAs } from "file-saver";
import {
  interactiveMapLogs,
  productAvailabilitiesLogs,
  downloadProductAvailabilitiesCSVAction,
  uploadDevelopmentCSV,
  productDevelopmentLogs,
  syncInteractiveEntryLogs,
  uploadAvailabilityCSV,
  fetchInteractiveMapQuarters,
} from "../../../appRedux/reducers/InteractiveMapsSlice";
import {
  DeleteTwoTone,
  DownloadOutlined,
  DownOutlined,
  EditTwoTone,
  MedicineBoxOutlined,
  UploadOutlined,
  UpOutlined,
} from "@ant-design/icons";
import { showConfirm, getRole } from "../../../helpers";
import "./interactiveMap.css";
import { InboxOutlined } from "@ant-design/icons";

const { Dragger } = Upload;

const InteractiveMap = (props) => {
  const isLoaded = useSelector((state) => state.interactiveMaps.isLoaded);
  const interactiveMapList = useSelector(
    (state) => state.interactiveMaps.InteractiveMapList,
  );
  const quarters = useSelector((state) => state.interactiveMaps.Quarters);
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [selectedStatusAt, setSelectedStatusAt] = useState(null);
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const [isUserLegal, setIsUserLegal] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [pdDropdownOpen, setPdDropdownOpen] = useState(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploadType, setUploadType] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setIsUserAdmin(() => {
      return getRole() === "ADMIN";
    });
    setIsUserLegal(() => {
      return getRole() === "LEGAL";
    });
    dispatch(interactiveMapLogs(true));
    dispatch(fetchInteractiveMapQuarters());
  }, []);

  // Default the quarter selection to the most recently completed quarter
  useEffect(() => {
    if (quarters && quarters.length && selectedStatusAt == null) {
      const defaultQuarter =
        quarters.find((q) => q.is_default) || quarters[0];
      setSelectedStatusAt(defaultQuarter.status_at);
    }
  }, [quarters]);

  useEffect(() => {
    let rowData = interactiveMapList && interactiveMapList.length
      ? interactiveMapList.map((value) => ({
        key: value.interactive_log_id,
        status: value.status ? value.status.toUpperCase() : "UNKNOWN",
        statusAt: value.status_at || "N/A",
        newImport: value.is_new_import === null ? "N/A" : (value.is_new_import ? "Yes" : "No"),
        nbEntriesUpdated: value.nb_entries_updated || 0,
        nbEntriesCreated: value.nb_entries_created || 0,
        type: value.type || "Unknown",
      }))
      : [];

    setData(rowData);
  }, [isLoaded, interactiveMapList]);

  const columns = [
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      ellipsis: true,
      sorter: (a, b) => a.status.localeCompare(b.status),
      render: (status) => <span>{status}</span>,
    },
    {
      title: "Status At",
      dataIndex: "statusAt",
      key: "statusAt",
      ellipsis: true,
      sorter: (a, b) => a.statusAt.localeCompare(b.statusAt),
      render: (statusAt) => <span>{statusAt}</span>,
    },
    {
      title: "New Import",
      dataIndex: "newImport",
      key: "newImport",
      ellipsis: true,
      sorter: (a, b) => a.newImport.localeCompare(b.newImport),
    },
    {
      title: "Nb Entries Created",
      dataIndex: "nbEntriesCreated",
      key: "nbEntriesCreated",
      ellipsis: true,
      sorter: (a, b) => a.nbEntriesCreated - b.nbEntriesCreated,
      render: (count) => <span>{count}</span>,
    },
    {
      title: "Nb Entries Updated",
      dataIndex: "nbEntriesUpdated",
      key: "nbEntriesUpdated",
      ellipsis: true,
      sorter: (a, b) => a.nbEntriesUpdated - b.nbEntriesUpdated,
      render: (count) => <span>{count}</span>,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      sorter: (a, b) => a.type.localeCompare(b.type),
    },
  ];

  const getInteractiveMaplogs = () => {
    dispatch(interactiveMapLogs(true));
  };

  const productAvailabilities = () => {
    dispatch(productAvailabilitiesLogs({}));
    getInteractiveMaplogs();
  };

  const productDevelopment = () => {
    const response = dispatch(productDevelopmentLogs({}));
    console.log(response);
    getInteractiveMaplogs();
  };

  const syncInteractiveEntry = () => {
    dispatch(syncInteractiveEntryLogs({}));
    getInteractiveMaplogs();
  };

  const handlePAMenuClick = ({ key }) => {
    if (key === "upload") {
      openUploadModal("PA");
    } else if (key === "download") {
      handleDownloadProductAvailabilitiesCSV();
    }
  };

  const handlePDMenuClick = ({ key }) => {
    if (key === "upload") {
      openUploadModal("PD");
    } else if (key === "download") {
      handleDownloadProductDevelopmentCSV();
    }
  };

  // The generate endpoints return a JSON `{ message }` (instead of CSV text)
  // when there is no data for the selected quarter. Detect that so we surface
  // the message rather than downloading a file containing the JSON.
  const getDownloadMessage = (payload) => {
    if (typeof payload !== "string") return null;
    try {
      const parsed = JSON.parse(payload);
      return parsed && parsed.message ? parsed.message : null;
    } catch (e) {
      return null;
    }
  };

  const handleDownloadProductAvailabilitiesCSV = async () => {
    if (!selectedStatusAt) {
      message.error("Please select a quarter first.");
      return;
    }
    try {
      const result = await dispatch(
        downloadProductAvailabilitiesCSVAction({ status_at: selectedStatusAt }),
      );
      if (result && result.payload) {
        const info = getDownloadMessage(result.payload);
        if (info) {
          message.info(info);
          return;
        }
        const blob = new Blob([result.payload], { type: 'text/csv;charset=utf-8;' });
        const filename = `product_availabilities_${selectedStatusAt}.csv`;
        saveAs(blob, filename);
      }
    } catch (error) {
      console.error('Error downloading Product Availabilities CSV:', error);
      message.error('Failed to download Product Availabilities CSV');
    }
  };

  const handleDownloadProductDevelopmentCSV = async () => {
    if (!selectedStatusAt) {
      message.error("Please select a quarter first.");
      return;
    }
    try {
      const result = await dispatch(
        productDevelopmentLogs({ status_at: selectedStatusAt }),
      );
      if (result && result.payload) {
        const info = getDownloadMessage(result.payload);
        if (info) {
          message.info(info);
          getInteractiveMaplogs();
          return;
        }
        const blob = new Blob([result.payload], { type: 'text/csv;charset=utf-8;' });
        const filename = `product_development_${selectedStatusAt}.csv`;
        saveAs(blob, filename);
      }
      getInteractiveMaplogs();
    } catch (error) {
      console.error('Error downloading Product Development CSV:', error);
      message.error('Failed to download Product Development CSV');
      getInteractiveMaplogs();
    }
  };

  const openUploadModal = (type) => {
    setUploadType(type);
    setUploadModalVisible(true);
    setFileList([]);
  };

  const handleFileChange = ({ fileList }) => {
    setFileList(fileList.slice(-1));
  };

  const handleUpload = async () => {
    if (!fileList.length) {
      message.error("Please select a CSV file to upload.");
      return;
    }

    const file = fileList[0].originFileObj;
    if (!file) {
      message.error("No file selected");
      return;
    }

    if (!selectedStatusAt) {
      message.error("Please select a quarter before uploading.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("status_at", selectedStatusAt);

    setUploading(true);
    try {
      if (uploadType === "PA") {
        await dispatch(uploadAvailabilityCSV(formData));
      } else if (uploadType === "PD") {
        await dispatch(uploadDevelopmentCSV(formData));
      }
      message.success("File uploaded successfully!");
      setUploadModalVisible(false);
      getInteractiveMaplogs();
    } catch (error) {
      console.error("Upload failed:", error);
      message.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const uploadProps = {
    onRemove: (file) => {
      setFileList([]);
    },
    beforeUpload: (file) => {
      const isCSV = file.type === 'text/csv' || file.name.endsWith('.csv');
      if (!isCSV) {
        message.error('You can only upload CSV files!');
        return Upload.LIST_IGNORE;
      }
      return false;
    },
    onChange: handleFileChange,
    fileList,
    maxCount: 1,
  };

  return (
    <div>
      <Row>
        <Col span={24}>
          <div className="gx-flex-row align-items-baseline">
            <h1 className="title gx-mb-4">
              <IntlMessages id="sidebar.interactive-map" />
            </h1>

            <Select
              className="gx-ml-auto gx-mr-2"
              style={{ minWidth: 150 }}
              placeholder="Select Quarter"
              value={selectedStatusAt}
              onChange={(value) => setSelectedStatusAt(value)}
              disabled={!isUserAdmin}
              options={(quarters || []).map((quarter) => ({
                value: quarter.status_at,
                label: quarter.quarter_name,
              }))}
            />

            <Dropdown
              menu={{
                items: [
                  {
                    key: 'upload',
                    label: 'Upload',
                    icon: <UploadOutlined />,
                    onClick: () => {
                      handlePAMenuClick({ key: 'upload' });
                      setDropdownOpen(false);
                    }
                  },
                  {
                    key: 'download',
                    label: 'Download',
                    icon: <DownloadOutlined />,
                    onClick: () => {
                      handlePAMenuClick({ key: 'download' });
                      setDropdownOpen(false);
                    }
                  }
                ],
                className: "PA-dropdown-menu"
              }}
              trigger={["click"]}
              onOpenChange={(flag) => setDropdownOpen(flag)}
              disabled={!isUserAdmin}
            >
              <Button type="primary" className="gx-mr-2 PA-dropdown-button">
                <Space>
                  Product Availabilities
                  {dropdownOpen ? <UpOutlined /> : <DownOutlined />}
                </Space>
              </Button>
            </Dropdown>

            <Dropdown
              menu={{
                items: [
                  {
                    key: 'upload',
                    label: 'Upload',
                    icon: <UploadOutlined />,
                    onClick: () => {
                      handlePDMenuClick({ key: 'upload' });
                      setPdDropdownOpen(false);
                    }
                  },
                  {
                    key: 'download',
                    label: 'Download',
                    icon: <DownloadOutlined />,
                    onClick: () => {
                      handlePDMenuClick({ key: 'download' });
                      setPdDropdownOpen(false);
                    }
                  }
                ],
                className: "PA-dropdown-menu"
              }}
              trigger={["click"]}
              onOpenChange={(flag) => setPdDropdownOpen(flag)}
              disabled={!isUserAdmin}
            >
              <Button type="primary" className="PA-dropdown-button" disabled={!isUserAdmin}>
                <Space>
                  Product Development
                  {pdDropdownOpen ? <UpOutlined /> : <DownOutlined />}
                </Space>
              </Button>
            </Dropdown>
            <Button
              id="sync-interactive-entry-btn"
              type="primary"
              onClick={syncInteractiveEntry}
              disabled={!isUserAdmin}
            >
              <MedicineBoxOutlined />{" "}
              <IntlMessages id="interactive_map.syncInteractiveEntry" />
            </Button>
          </div>
        </Col>
        <Table
          className="gx-table-responsive mpp-list-table"
          columns={columns}
          dataSource={data}
          pagination={false}
          bordered
          scroll={{ y: 'calc(100vh - 200px)' }}
          sticky={{ offsetHeader: 0 }}
        />
      </Row>
      <Modal
        title={`Upload ${uploadType === "PA" ? "Product Availabilities" : "Product Development"} CSV`}
        open={uploadModalVisible}
        onCancel={() => setUploadModalVisible(false)}
        onOk={handleUpload}
        okText="Upload"
        confirmLoading={uploading}
        destroyOnClose
      >
        <Dragger
          {...uploadProps}
          accept=".csv"
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Click or drag CSV file to this area to upload
          </p>
          <p className="ant-upload-hint">
            Support for a single CSV file upload.
          </p>
        </Dragger>
      </Modal>
    </div>
  );
};

export default InteractiveMap;